import React, { createContext, useContext } from "react";

/**
 * Context for sharing track management state and handlers
 * Reduces prop drilling through TrackGrid component
 */
const TrackContext = createContext({
  // State
  loading: false,
  tracks: [],
  allTracks: [],
  processedCarsMap: {},
  visibleCarsMap: null,
  foundCarMap: {},
  staleTracks: {},
  selectedShipmentIds: [],
  colorsLoaded: 0,
  loadedAccountIds: new Set(),

  // View Settings
  viewMode: "grid",
  compactMode: false,
  gridSize: 4,

  // Actions
  setLoading: () => {},
  setProcessedCarsMap: () => {},
  setSelectedShipmentIds: () => {},
  setSearchTerm: () => {},
  setAccountFilter: () => {},
  setProductFilter: () => {},
  setTrackFilter: () => {},
  setCompactMode: () => {},
  setGridSize: () => {},
  setViewMode: () => {},
  onAccountsLoaded: () => {},
  saveShipment: async () => {},
  refreshTracks: () => {},
  getTracks: () => {},
  getTrackCars: () => {},
  openShipment: () => {},
  isCustomer: false,
  customerAccountId: null,

  // Drag Handlers
  handleDragStart: () => {},
  handleDragOver: () => {},
  handleDragEnd: () => {},
});

// Track selection persistence (Crew TrackControl uses this)
export const TRACK_SELECTION_STORAGE_KEY = "TrackManagement:trackSelection";

export const readTrackSelection = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(TRACK_SELECTION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
};

export const writeTrackSelection = ({ leftTrackId, rightTrackId }) => {
  if (typeof window === "undefined") return;
  try {
    const payload = {
      leftTrackId: leftTrackId || null,
      rightTrackId: rightTrackId || null,
    };
    window.localStorage.setItem(
      TRACK_SELECTION_STORAGE_KEY,
      JSON.stringify(payload),
    );
  } catch (err) {
    // ignore storage failures
  }
};

export const useTrackContext = () => {
  const context = useContext(TrackContext);
  if (!context) {
    throw new Error("useTrackContext must be used within TrackProvider");
  }
  return context;
};

export const TrackProvider = ({ children, value }) => {
  return (
    <TrackContext.Provider value={value}>{children}</TrackContext.Provider>
  );
};
